
import { Client } from '../../../lib/supabase';

interface DistributionChartsProps {
  /** List of clients to be visualized. Defaults to an empty array to avoid runtime errors. */
  clients?: Client[];
}

/**
 * DistributionCharts component
 *
 * Renders two bar‑style charts:
 * 1. Distribution by Platform
 * 2. Distribution by Category
 *
 * The component is defensive: it works even when `clients` is undefined or an empty array.
 */
export default function DistributionCharts({
  clients = [], // default to empty array for robustness
}: DistributionChartsProps) {
  // ---------- Distribuição por Plataforma ----------
  // Initialise the map that will hold the count per platform.
  const platformCounts: Record<string, number> = {};

  // Populate platformCounts safely.
  clients.forEach((c) => {
    const platform = c.platform?.trim() || 'Outro';
    platformCounts[platform] = (platformCounts[platform] ?? 0) + 1;
  });

  // Convert to sortable array and order by count descending.
  const platforms = Object.entries(platformCounts).sort((a, b) => b[1] - a[1]);

  // Guard against an empty list – Math.max([]) returns -Infinity, so we ensure a minimum of 1.
  const maxPlatform = Math.max(...platforms.map((p) => p[1]), 1);

  // Colour mapping for known platforms; fallback to a generic gray.
  const platformColors: Record<string, string> = {
    TikTok: 'bg-[#004aad]',
    Instagram: 'bg-rose-500',
    YouTube: 'bg-red-500',
    Kwai: 'bg-orange-500',
    Outro: 'bg-gray-400',
  };

  // ---------- Distribuição por Categoria ----------
  const categoryCounts: Record<string, number> = {};

  clients.forEach((c) => {
    const cat = c.category?.trim() || 'Creator';
    categoryCounts[cat] = (categoryCounts[cat] ?? 0) + 1;
  });

  const categories = Object.entries(categoryCounts).sort((a, b) => b[1] - a[1]);

  const categoryColors = [
    'bg-[#004aad]',
    'bg-orange-400',
    'bg-amber-400',
    'bg-emerald-500',
    'bg-rose-400',
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Distribuição por Plataforma */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">
          Distribuição por Plataforma
        </h3>
        <div className="space-y-3">
          {platforms.map(([platform, count]) => (
            <div key={platform}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <i
                    className={`ri-${
                      platform.toLowerCase() === 'tiktok'
                        ? 'tiktok'
                        : platform.toLowerCase() === 'instagram'
                        ? 'instagram'
                        : platform.toLowerCase() === 'youtube'
                        ? 'youtube'
                        : 'global'
                    }-line text-sm text-gray-600`}
                  ></i>
                  <span className="text-sm font-medium text-gray-700">
                    {platform}
                  </span>
                </div>
                <span className="text-xs font-semibold text-gray-500">
                  {count} creators
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    platformColors[platform] || 'bg-gray-400'
                  }`}
                  style={{ width: `${(count / maxPlatform) * 100}%` }}
                ></div>
              </div>
            </div>
          ))}
          {platforms.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">
              Sem dados
            </p>
          )}
        </div>
      </div>

      {/* Distribuição por Categoria */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">
          Distribuição por Categoria
        </h3>
        <div className="space-y-3">
          {categories.map(([category, count], idx) => (
            <div key={category} className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span
                  className={`w-2.5 h-2.5 rounded-full ${
                    categoryColors[idx % categoryColors.length]
                  }`}
                ></span>
                <span className="text-sm font-medium text-gray-700">
                  {category}
                </span>
              </div>
              <span
                className={`text-xs font-semibold px-3 py-1 rounded-full text-white ${
                  categoryColors[idx % categoryColors.length]
                }`}
              >
                {count} creators
              </span>
            </div>
          ))}
          {categories.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">
              Sem dados
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
